import { api } from "@/lib/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    DriverCode,
    DriverCodeResponse,
    DriverCodeParams,
} from "@/interface/standard/st_driver_code";
import { toast } from "sonner";

/**
 * 모든 기사 코드 조회
 * @param params 페이지 번호, 페이지 크기, 검색어, 정렬
 * @returns 기사 코드 데이터
 */
export const useDriverCode = (params: DriverCodeParams = {}) => {
    const { page = 0, size = 20, search, mobileTel, sort, employmentStatus, exactMatch } = params;

    return useQuery<DriverCodeResponse>({
        queryKey: ["driverCode", page, size, search, mobileTel, sort, employmentStatus, exactMatch],
        queryFn: async () => {
            const response = await api.get("/st_driver_code", {
                params: {
                    page,
                    size,
                    name: search,
                    mobileTel,
                    sort,
                    employmentStatus,
                    exactMatch,
                },
            });
            return response.data;
        },
        staleTime: 1000 * 60, // 1분간 캐시 유지
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

export const useDriverCodeInsert = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Partial<DriverCode>) => {
            const response = await api.post("/st_driver_code", data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["driverCode"] });
            toast.success("기사 코드가 추가되었습니다.");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "기사 코드 추가에 실패했습니다.");
        },
    });
};

export const useDriverCodeDelete = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (stDriverCode: string) => {
            const response = await api.delete(`/st_driver_code/${stDriverCode}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["driverCode"] });
            toast.success("기사 코드가 삭제되었습니다.");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "기사 코드 삭제에 실패했습니다.");
        },
    });
};

export const useUpdateDriverCode = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ stDriverCode, data }: { stDriverCode: string; data: Partial<DriverCode> }) => {
            const response = await api.put(`/st_driver_code/${stDriverCode}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["driverCode"] });
            toast.success("기사 코드가 수정되었습니다.");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "기사 코드 수정에 실패했습니다.");
        },
    });
};

/**
 * 만료 예정 기사 조회 (1일 전 + 이미 만료된 기사)
 */
export const useExpiringDrivers = () => {
    return useQuery<DriverCode[]>({
        queryKey: ["expiringDrivers"],
        queryFn: async () => {
            const response = await api.get("/st_driver_code/expiring");
            return response.data;
        },
        staleTime: 1000 * 60 * 5, // 5분 캐시
        refetchOnWindowFocus: false,
    });
};  